import { useEffect, useMemo, useState } from 'react'
import { MdOutlineModeEditOutline } from 'react-icons/md'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Dispatch } from 'redux'
import { listOfRoles } from 'components/WithRoleAuthorization/WithRoleAuthorization'
import { notificationStatus } from 'constants/notificationStatus'
import { ROUTES_NAMES } from 'constants/name.routes'
import { NotificationAction, NotificationReducer } from 'types/notification'
import { RootReducerState } from 'types/global'
import NotificationActions from 'actions/Notification'
import PreviewResult from '../../NotificationsTreatmentsSteps/AccountabilityMatrix/previewResult'
import { checkboxStyle, EditButton, MainForm, Subtitle, Title } from '../GroupActionPlan/styles'
import { GroupTreatmentsStep4Props } from '../GroupActionPlan/helpers'
import { HelpDescription } from 'pages/NotificationTreatment/NotificationsTreatmentsSteps/AccountabilityMatrix/styles'
import { hasAdverseEventsOrImageRisk } from 'utils/notifyEvents'
import { isNoDamageOrNearMissOrNotifiableDamage } from 'utils/damage'
import ActionStatus from 'components/ActionStatus'

const canEditGroupAccountability = ({
    loggedInUserIsResponsible,
    isSameLocation,
    isSameSector,
    notificationStatusState
}: {
    loggedInUserIsResponsible: boolean
    isSameLocation: boolean
    isSameSector: boolean
    notificationStatusState: string
}) => {
    if (notificationStatusState === notificationStatus[3].label) {
        return [listOfRoles.admin, listOfRoles.location_qa]
    }

    return [
        ...(loggedInUserIsResponsible ? [listOfRoles.admin] : []),
        ...(isSameLocation && isSameSector && loggedInUserIsResponsible
            ? [listOfRoles.location_manager]
            : []),
        ...(isSameLocation && loggedInUserIsResponsible
            ? [listOfRoles.location_qa]
            : [])
    ]
}

const GroupAccountabilityMatrix = ({
    id,
    tooltipAlert,
    currentUser,
    statusIsArchivedOrFinished,
    notificationById,
    notificationStatusState,
    groupData,
    loggedInUserIsResponsible = true
}: GroupTreatmentsStep4Props) => {
    const navigate = useNavigate()
    const notificationDispatch: Dispatch<NotificationAction> = useDispatch()
    const notificationSelector: NotificationReducer = useSelector((state: RootReducerState) => state.notificationsReducer)
    const [notAllowed, setNotAllowed] = useState(false)
    const [unrealizedOptional, setUnrealizedOptional] = useState(false)

    const accountabilityMatrixMemo = useMemo(() => {
        if (notificationSelector?.accountabilityMatrix) {
            return notificationSelector?.accountabilityMatrix[0]
        }
    }, [notificationSelector?.accountabilityMatrix])

    useEffect(() => {
        if (!notAllowed) {
            setUnrealizedOptional(accountabilityMatrixMemo?.u_do_not_perform_optional == 'true')
        }
    }, [notAllowed, accountabilityMatrixMemo])

    useEffect(() => {
        console.log("Loading Accountability Matrix...")
        if (groupData?.notifications?.length > 0) {
            const firstNotificationId = groupData.notifications[0]?.sys_id?.value
            if (firstNotificationId) {
                NotificationActions.getAccountabilityMatrixByNotificationId(notificationDispatch, firstNotificationId)
            }
        }
    }, [currentUser])

    const isSameLocation = useMemo(() => {
        if (notificationById?.location_incident?.value) {
            return (
                currentUser?.locations &&
                currentUser.locations.filter((i) => i.sys_id === notificationById.location_incident?.value).length > 0
            )
        }
        return true
    }, [currentUser, notificationById])

    const isSameSector = useMemo(() => {
        if (notificationById?.location_incident?.value && notificationById?.incident_sector?.value) {
            return (
                currentUser?.locations &&
                currentUser.locations
                    .filter((i) => i.sys_id === notificationById.location_incident?.value)[0]
                    ?.sectors.filter((i) => i.sys_id === notificationById.incident_sector?.value).length > 0
            )
        }
        return true
    }, [currentUser, notificationById])

    useEffect(() => {
        const allowed = canEditGroupAccountability({
            loggedInUserIsResponsible,
            isSameLocation,
            isSameSector,
            notificationStatusState
        }).includes(currentUser?.profile)

        setNotAllowed(!(allowed && !statusIsArchivedOrFinished))
    }, [
        loggedInUserIsResponsible,
        isSameLocation,
        isSameSector,
        notificationStatusState,
        currentUser?.profile,
        statusIsArchivedOrFinished
    ])

    const hideOrShowCheckUnrealizedOptional = useMemo(() => {
        let imageRiskValue = notificationSelector.notificationById?.u_image_risk?.value
        let damage = notificationSelector.notificationById?.damage_level?.value
        const isImageRisk = imageRiskValue === 'true' || imageRiskValue === 'u_image_risk_yes'
        const hasNeverEvents = notificationSelector?.notificationById?.u_never_events_or_sentinel_event?.value == 'yes'

        const isOlder = notificationSelector?.notificationById?.u_probability_occurence?.value === ''
        if (isOlder) {
            const checkboxRequired = !isImageRisk && isNoDamageOrNearMissOrNotifiableDamage(damage)
            return checkboxRequired
        }

        const stateAccountMatrix = notificationSelector?.notificationProcessRequiredStepsRules?.accountabilityMatrixMandatory
        return !hasNeverEvents && ((!hasAdverseEventsOrImageRisk({ damageLevelValue: damage, imageRiskValue: imageRiskValue })
            || stateAccountMatrix === "Optional"))

    }, [currentUser])

    const onChangeCheckedNotPerform = async (checkedValue: boolean) => {
        if (!groupData) return
        setUnrealizedOptional(checkedValue)

        try {
            const hasExisting = Boolean(accountabilityMatrixMemo?.sys_id)
            const questions = {
                u_question_01: '',
                u_question_02: '',
                u_question_03: '',
                u_question_04: '',
                u_question_05: '',
                u_question_06: '',
                u_question_07: '',
                u_question_08: '',
                u_question_09: '',
                u_question_10: '',
                u_matrix_result: ''
            }

            const notificationGroup = groupData.notifications
            const records = await Promise.all(
                notificationGroup.map(async (notificacao) => {

                    const matrix = hasExisting
                        ? await NotificationActions.getAccountabilityMatrixByNotificationId(
                            notificationDispatch,
                            notificacao.sys_id.value
                        )
                        : undefined

                    return {
                        ...(matrix?.sys_id && { sys_id: matrix.sys_id }),
                        u_hospital_incident: notificacao.sys_id,
                        u_do_not_perform_optional: checkedValue ? 'true' : 'false',
                        ...questions
                    }
                })
            )

            const payload = { records, options: { stopOnError: false } }
            const action = hasExisting
                ? NotificationActions.updateAccountabilityMatrixBatch
                : NotificationActions.createAccountabilityMatrixBatch

            await action(notificationDispatch, payload)

            await NotificationActions.getAccountabilityMatrixByNotificationId(notificationDispatch, groupData.notifications[0]?.sys_id?.value)
        } catch (error) {
            console.error('Erro ao alterar preenchimento da Matriz em grupo:', error)
        }
    }

    const navigateRoute = () => {
        navigate(`${ROUTES_NAMES.notifications}/group_accountability_matrix/${id}`)
    }
    
    return (
        <MainForm data-testid="test-accountabilitmatrix">
            <div className="px-8 flex flex-row gap-12 mt-1 min-h-[500px]">
                <fieldset disabled={notAllowed}>
                    {tooltipAlert && (
                        <div
                            className="px-6"
                            data-testid="test-tooltip-alert-3"
                        >
                            <ActionStatus
                                text={tooltipAlert}
                                status={1}
                            />
                        </div>
                    )}
                    <div className="flex flex-col">
                        <div className="flex flex-row gap-10 mt-1 mb-10">
                            <div className="flex justify-center items-center gap-3">
                                <Title>Matriz de responsabilização</Title>
                            </div>
                        </div>

                        <Subtitle>
                            A Matriz de Responsabilização será utilizada na análise de incidentes quando existir necessidade de definir e formalizar responsabilidades entre
                            profissionais, equipes, setores ou áreas de apoio, especialmente em eventos que envolvam falhas de processo, comunicação ou interfaces assistenciais.
                        </Subtitle>

                        <HelpDescription>
                            <p className="mb-2">Conforme a POLDOR 007, sua aplicação é indicada: </p>
                            <div className="w-full">
                                <ul className="list-disc list-outside pl-5 marker:font-bold marker:text-lg space-y-2 leading-relaxed">
                                    <li>
                                        Em notificações classificadas como circunstância notificável, near miss, incidente sem dano, com dano leve ou incidente com dano moderado, quando a Matriz de Risco apontar maior complexidade organizacional (pontuação maior que 5)
                                    </li>
                                    <li>
                                        Em eventos com dano grave, óbito, evento sentinela/never event ou risco de imagem é obrigatório.
                                    </li>
                                </ul>
                            </div>
                        </HelpDescription>

                        <div className="flex flex-row gap-12 mt-6 mb-10">
                            <EditButton
                                type="button"
                                className={`${unrealizedOptional || notAllowed
                                    ? 'bg-disabled text-textSelect font-normal'
                                    : ''
                                    }`}
                                disabled={unrealizedOptional || notAllowed}
                                onClick={navigateRoute}
                            >
                                <div className="w-[202px] flex justify-center items-center gap-3">
                                    <MdOutlineModeEditOutline
                                        size={18}
                                        color={`${!unrealizedOptional ||
                                            notAllowed
                                            ? 'white'
                                            : '#4F4F4F'
                                            }`}
                                    />
                                    <span className="inline-block align-middle">
                                        {!accountabilityMatrixMemo?.sys_id
                                            ? 'Preencher matriz'
                                            : 'Editar matriz'}
                                    </span>
                                </div>
                            </EditButton>

                            {hideOrShowCheckUnrealizedOptional &&
                                <div className="mt-2 block min-h-[1.5rem] pl-[1.5rem]">
                                    <input
                                        onChange={(e) =>
                                            onChangeCheckedNotPerform(
                                                e.target.checked
                                            )
                                        }
                                        checked={unrealizedOptional}
                                        name="u_do_not_perform_optional"
                                        className={`${checkboxStyle}  ${notAllowed
                                            ? 'opacity-40'
                                            : 'hover:cursor-pointer'
                                            }`}
                                        type="checkbox"
                                        id="checkUnrealizedOptional"
                                    />
                                    <label
                                        className={`inline-block pl-[0.15rem]  ${notAllowed
                                            ? 'opacity-40'
                                            : 'hover:cursor-pointer'
                                            }`}
                                        htmlFor="checkUnrealizedOptional">
                                        Não realizar o preenchimento
                                    </label>
                                </div>}
                        </div>

                        {accountabilityMatrixMemo?.u_matrix_result !== '' ? (
                            <PreviewResult
                                accountabilityMatrix={
                                    accountabilityMatrixMemo
                                }
                            ></PreviewResult>
                        ) : (
                            <></>
                        )}
                    </div>
                </fieldset>
            </div>
        </MainForm>
    )
}

export { GroupAccountabilityMatrix }
